import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "emailjs-com";
import "../styles/Quote.css";
import { company } from "../data/site";

const SITE_KEY = process.env.REACT_APP_GOOGLE_SITE_KEY;
const EMAILJS = {
	service: process.env.REACT_APP_EMAILJS_SERVICE_ID,
	template: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
	publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

const empty = { name: "", company: "", email: "", phone: "", message: "", website: "" };

// Resolve with null instead of hanging if reCAPTCHA can't load (blocked script, bad key).
const withTimeout = (promise, ms) =>
	Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

export default function Quote() {
	const recaptchaRef = useRef(null);
	const [form, setForm] = useState(empty);
	const [status, setStatus] = useState("idle"); // idle | sending | sent | error

	const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

	const onSubmit = async (e) => {
		e.preventDefault();
		if (form.website) return; // honeypot filled: silently drop bot submissions

		setStatus("sending");
		try {
			// Invisible reCAPTCHA v2. The token is verified server-side by EmailJS when
			// reCAPTCHA is enabled on the template (EmailJS dashboard → template → Settings).
			let token = null;
			if (SITE_KEY && recaptchaRef.current) {
				token = await withTimeout(recaptchaRef.current.executeAsync(), 10000);
				recaptchaRef.current.reset();
			}

			const { website, ...fields } = form;
			await emailjs.send(
				EMAILJS.service,
				EMAILJS.template,
				{ ...fields, reply_to: fields.email, ...(token ? { "g-recaptcha-response": token } : {}) },
				EMAILJS.publicKey
			);
			setStatus("sent");
			setForm(empty);
		} catch (err) {
			console.error("Quote form error:", err);
			setStatus("error");
		}
	};

	return (
		<>
			<title>Request a quote | B & H Machining</title>
			<meta
				name="description"
				content="Request a quote for precision ceramic, quartz, glass or metal parts from B & H Machining in San Carlos, CA."
			/>

			<section className="page-head">
				<div className="wrap">
					<h1>Request a quote</h1>
					<p className="lede">
						Tell us about your part and we'll get back to you with pricing and lead time.
					</p>
				</div>
			</section>

			<section className="wrap quote">
				<form className="quote__form" onSubmit={onSubmit}>
					<div className="field-row">
						<label className="field">
							<span>Name</span>
							<input name="name" value={form.name} onChange={onChange} required autoComplete="name" />
						</label>
						<label className="field">
							<span>
								Company <em>optional</em>
							</span>
							<input name="company" value={form.company} onChange={onChange} autoComplete="organization" />
						</label>
					</div>
					<div className="field-row">
						<label className="field">
							<span>Email</span>
							<input
								type="email"
								name="email"
								value={form.email}
								onChange={onChange}
								required
								autoComplete="email"
							/>
						</label>
						<label className="field">
							<span>
								Phone <em>optional</em>
							</span>
							<input type="tel" name="phone" value={form.phone} onChange={onChange} autoComplete="tel" />
						</label>
					</div>
					<label className="field">
						<span>Project details</span>
						<textarea
							name="message"
							rows="7"
							value={form.message}
							onChange={onChange}
							required
							placeholder="Material, quantity, key dimensions and tolerances, and when you need the parts."
						/>
					</label>

					{/* Honeypot: hidden from people and screen readers, bots tend to fill it */}
					<div className="hp" aria-hidden="true">
						<label>
							Website
							<input name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
						</label>
					</div>

					{SITE_KEY && <ReCAPTCHA sitekey={SITE_KEY} size="invisible" badge="inline" ref={recaptchaRef} />}

					<div className="quote__submit">
						<button type="submit" className="button button--primary" disabled={status === "sending"}>
							{status === "sending" ? "Sending request…" : "Send request"}
						</button>
						<p className="quote__status" role="status" aria-live="polite">
							{status === "sent" && "Request sent. We'll be in touch soon."}
							{status === "error" && (
								<span className="is-error">
									The request didn't go through. Try again, or email us at{" "}
									<a href={`mailto:${company.email}`}>{company.email}</a>.
								</span>
							)}
						</p>
					</div>

					<p className="quote__fineprint">
						We use your details only to respond to this request. See our{" "}
						<Link to="/privacy">privacy policy</Link>.
					</p>
				</form>

				<aside className="quote__aside" aria-label="Quote tips and direct contact">
					<h2>What helps us quote quickly</h2>
					<ul>
						<li>Material and grade, such as 99.5% alumina or fused quartz</li>
						<li>Quantity, and whether this is a prototype or a production order</li>
						<li>Critical dimensions, tolerances and surface finish</li>
						<li>Your target delivery date</li>
					</ul>
					<h2>Have drawings?</h2>
					<p>
						Email PDF or STEP files to{" "}
						<a href={`mailto:${company.email}?subject=Quote%20request%20drawings`}>{company.email}</a>{" "}
						and mention your name so we can match them to your request.
					</p>
					<h2>Prefer to talk?</h2>
					<p>
						Call <a href={company.phoneHref}>{company.phone}</a>.
					</p>
				</aside>
			</section>
		</>
	);
}
